/** Small formatting helpers shared across views. */

/**
 * Compact relative time, e.g. `3m`, `4h`, `2d`, `5w`.
 *
 * @param {string|null|undefined} iso ISO-8601 timestamp from the API.
 * @returns {string} Empty string when `iso` is missing.
 */
export function ago(iso) {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const seconds = Math.max(0, (Date.now() - then) / 1000)
  if (seconds < 60) return `${Math.floor(seconds)}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
  if (seconds < 86400 * 14) return `${Math.floor(seconds / 86400)}d`
  if (seconds < 86400 * 365) return `${Math.floor(seconds / (86400 * 7))}w`
  return `${Math.floor(seconds / (86400 * 365))}y`
}

/** Absolute local timestamp, minute precision. */
export function stamp(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(undefined, {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Display name for a user id, using a map built from `GET /users`.
 *
 * @param {Map<number, object>} users
 * @param {number|null|undefined} id
 * @returns {string}
 */
export function userName(users, id) {
  if (id == null) return 'unassigned'
  const user = users.get(id)
  return user ? user.name || user.email : `user ${id}`
}

/**
 * Render a count with a correctly pluralised noun: `1 asset`, `2 assets`.
 *
 * Every count in this UI is data-driven, so "1 assets assigned" is reachable
 * from real state, not a hypothetical. Pass `plural` for nouns the -s rule
 * gets wrong.
 *
 * @param {number} n count
 * @param {string} singular noun in its singular form
 * @param {string} [plural] override for irregular plurals
 * @returns {string} e.g. `"1 asset"` / `"3 assets"`
 */
export function count(n, singular, plural) {
  return `${n} ${n === 1 ? singular : plural || `${singular}s`}`;
}
